/**
 * Thai message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: polite-neutral with คุณ, no gendered
 * politeness particles (ครับ/ค่ะ) in UI strings (locked in l10n/locales.md).
 *
 * Platform terms follow Thai UI conventions (การตั้งค่า for settings,
 * ธีม for theme, การสนทนา for conversation, จับคู่ for pairing,
 * ล้าง for dismiss, การสะท้อน for mirror, ตัดการเชื่อมต่อ for disconnect).
 * "Glance" is the untranslated feature brand name (glossary D1); "คลิป"
 * renders the D2 clip loanword in Thai script. Thai has no inter-word
 * spaces, but a space is kept around inline placeholders and Latin/number
 * runs per Thai UI convention.
 *
 * Plural notes: Thai is an `other`-only CLDR locale — nouns take no
 * grammatical plural, so every `selectPlural("th", …)` call supplies only
 * an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "เชื่อมต่อแล้ว",
    statusReconnecting: "กำลังเชื่อมต่อใหม่…",
    statusDisconnected: "ตัดการเชื่อมต่อแล้ว",
    openSettings: "เปิดการตั้งค่า",
    pairedFallbackDevice: "โทรศัพท์ของคุณ",
    publicModeToggleEnable: "ซ่อนเนื้อหาการแจ้งเตือน",
    publicModeToggleDisable: "แสดงเนื้อหาการแจ้งเตือน",
  },

  notifications: {
    replyAriaLabel: "ตอบกลับ",
    replyPlaceholder: "ตอบกลับ…",
    replySend: "ส่ง",
    sent: "ส่งแล้ว",
    failedToSend: "ส่งไม่สำเร็จ",
    unread: "ยังไม่อ่าน",
    dismiss: "ล้าง",
    notificationImageAlt: "รูปภาพการแจ้งเตือน",
  },

  conversations: {
    replyAriaLabel: "ตอบกลับ",
    replyPlaceholder: "ตอบกลับ…",
    replySend: "ส่ง",
    sent: "ส่งแล้ว",
    failedToSend: "ส่งไม่สำเร็จ",
    dismiss: "ล้างการสนทนา",
    messagesListLabel: "ข้อความในการสนทนานี้",
    deviceOwner: "คุณ",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "ไม่มีข้อความที่ยังไม่อ่าน"
        : `ยังไม่อ่าน ${count} ${selectPlural("th", count, { other: "ข้อความ" })}`,
  },

  list: {
    today: "วันนี้",
    yesterday: "เมื่อวาน",
    searchNotifications: "ค้นหาการแจ้งเตือน",
    clearSearch: "ล้างการค้นหา",
    clearAll: "ล้างทั้งหมด",
    markAllRead: "ทำเครื่องหมายว่าอ่านทั้งหมด",
    groupCheckboxLabel: "จัดกลุ่ม",
    noNotificationsTitle: "ยังไม่มีการแจ้งเตือน",
    noNotificationsDesc: "ข้อความใหม่ที่สะท้อนจากโทรศัพท์ของคุณจะปรากฏที่นี่",
    noMatchesTitle: "ไม่มีรายการที่ตรงกัน",
    notificationsList: "การแจ้งเตือน",
    privacyAppList: "การแจ้งเตือนตามแอป",
    clear: "ล้าง",
    clearMatches: (count: number): string =>
      `ล้างรายการที่ตรงกัน ${count} ${selectPlural("th", count, { other: "รายการ" })}`,
    noMatchesDesc: (query: string): string => `ไม่มีการแจ้งเตือนที่ตรงกับ “${query}”`,
    appNotificationsGroup: (appName: string): string => `การแจ้งเตือน ${appName}`,
    dismissAllFrom: (appName: string): string => `ล้างทั้งหมดจาก ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("th", count, { other: "ข้อความ" })
          : kind === "calls"
            ? selectPlural("th", count, { other: "การโทร" })
            : selectPlural("th", count, { other: "การแจ้งเตือน" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "ล้าง",
    kbdNavigate: "เลื่อน",
    kbdOrSeparator: "หรือ",
  },

  settings: {
    back: "กลับไปยังการแจ้งเตือน",
    settings: "การตั้งค่า",
    appearance: "ลักษณะที่ปรากฏ",
    theme: "ธีม",
    themeAuto: "อัตโนมัติ",
    themeLight: "สว่าง",
    themeDark: "มืด",
    language: "ภาษา",
    languageSystemDefault: "ค่าเริ่มต้นของระบบ",
    notificationsSection: "การแจ้งเตือน",
    osNotifications: "การแจ้งเตือนของ OS",
    callNotifications: "การแจ้งเตือนการโทร",
    privacySection: "ความเป็นส่วนตัว",
    analyticsToggle: "สถิติการใช้งานแบบไม่ระบุตัวตน",
    analyticsToggleDescription: "นับจำนวนและเหตุการณ์สำคัญเท่านั้น — ไม่ใช่การแจ้งเตือนของคุณ",
    connection: "การเชื่อมต่อ",
    status: "สถานะ",
    statusConnected: "เชื่อมต่อแล้ว",
    statusReconnecting: "กำลังเชื่อมต่อใหม่…",
    statusDisconnected: "ตัดการเชื่อมต่อแล้ว",
    disconnectFromPhone: "ตัดการเชื่อมต่อจากโทรศัพท์",
    disconnectConfirm: "คุณแน่ใจหรือไม่? การดำเนินการนี้จะยกเลิกการจับคู่อุปกรณ์ของคุณ",
    yesDisconnect: "ใช่ ตัดการเชื่อมต่อ",
    cancel: "ยกเลิก",
    aboutAndSupport: "เกี่ยวกับและการสนับสนุน",
    version: "เวอร์ชัน",
    encryption: "การเข้ารหัส",
    encryptionValue: "E2E (NaCl)",
    needHelp: "ต้องการความช่วยเหลือ? อีเมลถึง __EMAIL__",
    disconnectFailed: "ตัดการเชื่อมต่อไม่สำเร็จ — โปรดลองอีกครั้ง",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `ตัดการเชื่อมต่อแล้ว แต่การล้างข้อมูลในเครื่องล้มเหลว: ${errorMsg} โปรดโหลดหน้าใหม่`,
    disconnectLocalCleanupFailed: "ตัดการเชื่อมต่อแล้ว แต่การล้างข้อมูลในเครื่องล้มเหลว โปรดโหลดหน้าใหม่",
  },

  pairing: {
    enterCodeInstruction: "ป้อนรหัสนี้ในแอป Reflecto",
    pairedSuccessfully: "จับคู่สำเร็จ!",
    pairingFailed: "การจับคู่ล้มเหลว",
    requestingPairingCode: "กำลังขอรหัสจับคู่…",
    codeExpired: "รหัสหมดอายุ",
    tryAgain: "ลองอีกครั้ง",
    failedToStartPairing: "เริ่มการจับคู่ไม่สำเร็จ",
    needAppText: "ต้องการแอป Android ใช่ไหม? อีเมลถึง __EMAIL__ เพื่อขอสิทธิ์เข้าถึง",
  },

  dnd: {
    title: "ห้ามรบกวน",
    description: "หยุดการแจ้งเตือนบนอุปกรณ์นี้ชั่วคราวเป็นเวลาหนึ่งชั่วโมง",
    snooze1h: "หยุดชั่วคราว 1 ชั่วโมง",
    snoozeActiveUntil: (formattedTime: string): string => `ใช้งานอยู่จนถึง ${formattedTime}`,
    cancelSnooze: "ยกเลิก",
  },

  connectors: {
    title: "บริการที่เชื่อมต่อ",
    emptyBody:
      "ยังไม่มีบริการที่เชื่อมต่อ เชื่อมต่อ ChatGPT หรือ Claude จากการตั้งค่าตัวเชื่อมต่อของบริการนั้น แล้วอนุมัติรหัสบนโทรศัพท์ของคุณ",
    scopeSend: "ส่งการแจ้งเตือนได้",
    added: (date: string): string => `เพิ่มเมื่อ ${date}`,
    lastUsed: (relative: string): string => `ใช้ล่าสุด ${relative}`,
    neverUsed: "ไม่เคยใช้",
    revoke: "ตัดการเชื่อมต่อ",
    revokeConfirm: (label: string): string => `ตัดการเชื่อมต่อ ${label} ใช่ไหม? จะเสียการเข้าถึงทันที`,
    revokeAction: "ตัดการเชื่อมต่อ",
    revokeConfirmLabel: (label: string): string => `ตัดการเชื่อมต่อ ${label}`,
    cancel: "ยกเลิก",
    loadError: "โหลดบริการที่เชื่อมต่อไม่สำเร็จ โปรดลองอีกครั้ง",
    revokeError: "ตัดการเชื่อมต่อบริการไม่สำเร็จ โปรดลองอีกครั้ง",
    retry: "ลองใหม่",
  },

  feedback: {
    title: "ส่งความคิดเห็น",
    close: "ปิด",
    messageLabel: "ข้อความของคุณ",
    messagePlaceholder: "พบบั๊ก มีไอเดีย หรือรู้สึกว่ามีอะไรผิดปกติ? บอกเราได้เลย",
    categoryLabel: "หมวดหมู่",
    categoryBug: "บั๊ก",
    categoryIdea: "ไอเดีย",
    categoryOther: "อื่นๆ",
    emailLabel: "อีเมล (ไม่บังคับ เฉพาะเมื่อคุณต้องการการตอบกลับ)",
    emailPlaceholder: "you@example.com",
    emailHelp: "เราจะไม่เพิ่มคุณลงในรายชื่อใดๆ",
    disclosure:
      "ข้อความของคุณจะถูกส่งไปยังเซิร์ฟเวอร์ของ Reflecto และบันทึกไว้ในระบบติดตามปัญหาภายในของเรา เราจะแนบเวอร์ชันแอปของคุณและรหัสอุปกรณ์ที่กำหนดระหว่างการจับคู่เพื่อให้เราเชื่อมโยงรายงานได้ เราไม่เห็นการแจ้งเตือน รายชื่อผู้ติดต่อ หรือข้อมูลส่วนตัวใดๆ ของคุณ อีเมลไม่บังคับ — กรอกเฉพาะเมื่อคุณต้องการการตอบกลับ เราจะไม่เพิ่มคุณลงในรายชื่อใดๆ",
    submit: "ส่งความคิดเห็น",
    sending: "กำลังส่ง…",
    success: "ขอบคุณ — มีคนอ่านจริงๆ โดยปกติภายใน 24 ชม.",
    error: "ส่งไม่สำเร็จ โปรดลองอีกครั้ง",
    done: "เสร็จสิ้น",
    sendFeedback: "ส่งความคิดเห็น",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "ปิด",
    aiSummaryTag: "สรุปโดย AI",
    errorAi: "ไม่สามารถสร้างสรุปได้ในขณะนี้",
    retryCta: "ลองอีกครั้ง",
    downloadHeading: "รับสรุปโดย AI",
    downloadBody: "ตามให้ทันสิ่งที่คุณพลาดไปด้วยการสรุปโดย AI บนอุปกรณ์ ทำงานบนอุปกรณ์ของคุณทั้งหมด",
    downloadCta: "ดาวน์โหลดโมเดล",
    downloadPrivacyNote: "ดาวน์โหลดเพียงครั้งเดียว เซิร์ฟเวอร์ไม่เคยเห็นการแจ้งเตือนของคุณ",
    downloadingHeading: "กำลังดาวน์โหลดโมเดล…",
    caughtUp: "คุณตามทันทุกอย่างแล้ว — ตอนนี้ไม่มีอะไรที่ต้องการความสนใจจากคุณ",
    needsYou: "ต้องการความสนใจ",
    quiet: "อื่นๆ",
    scanning: (pending: number): string => `กำลังสรุป ${pending} รายการ…`,
    jump: "เปิดการแจ้งเตือน",
    affordanceReply: "ตอบกลับ",
    affordanceOpen: "เปิด",
    affordanceCopyOtp: "คัดลอกรหัส",
    buttonLabel: "Glance",
  },

  update: {
    title: "มีอัปเดตพร้อมใช้งาน",
    description: "Reflecto เวอร์ชันใหม่พร้อมแล้ว โหลดหน้าใหม่เพื่ออัปเดต",
    reload: "โหลดใหม่",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `เราตั้งชื่ออุปกรณ์นี้ว่า “${name}”`,
    cardLooksGood: "ใช้ได้เลย",
    cardEdit: "แก้ไข",
    cardAriaLabel: "ยืนยันชื่ออุปกรณ์",
    renameTitle: "เปลี่ยนชื่ออุปกรณ์นี้",
    renameLabel: "ชื่ออุปกรณ์",
    renameSave: "บันทึก",
    renameCancel: "ยกเลิก",
    renameFailed: "เปลี่ยนชื่อไม่สำเร็จ โปรดลองอีกครั้ง",
  },

  collision: {
    title: (name: string): string => `คุณมี “${name}” อยู่แล้ว`,
    subtitle: "จะเรียกเครื่องนี้ว่าอะไรดี?",
    chipWork: (name: string): string => `${name} ที่ทำงาน`,
    chipHome: (name: string): string => `${name} ที่บ้าน`,
    chipPersonal: (name: string): string => `${name} ส่วนตัว`,
    customLabel: "ชื่อกำหนดเอง",
    save: "บันทึก",
    useAnyway: "ใช้ชื่อเดิมต่อไป",
    saveFailed: "บันทึกชื่อใหม่ไม่สำเร็จ โปรดลองอีกครั้ง",
    ariaLabel: "แก้ไขชื่ออุปกรณ์ที่ซ้ำกัน",
  },

  clipboard: {
    title: "คลิปบอร์ด",
    empty: "คลิปจากอุปกรณ์อื่นของคุณจะปรากฏที่นี่ แตะเพื่อคัดลอก",
    from: (label: string): string => `จาก ${label}`,
    copy: "คัดลอกไปยังคลิปบอร์ด",
    copied: "คัดลอกแล้ว",
    reveal: "แสดง",
    hide: "ซ่อน",
    maskedHint: "เนื้อหาถูกซ่อนไว้ — กด แสดง เพื่อแสดง",
    sensitiveChip: "ละเอียดอ่อน",
    cloudChip: "จากโทเคน API ของคุณ",
    sendPlaceholder: "วางข้อความเพื่อส่ง…",
    sendCta: "ส่ง",
    sendSensitiveToggle: "ทำเครื่องหมายว่าละเอียดอ่อน",
    sendSuccess: (n: number): string =>
      `ส่งไปยัง ${n} ${selectPlural("th", n, { other: "อุปกรณ์" })} แล้ว`,
    sendPartial: (n: number, total: number): string =>
      `ส่งถึง ${n}/${total} ${selectPlural("th", total, { other: "อุปกรณ์" })} แล้ว`,
    sendNoDevices: "ไม่มีอุปกรณ์ที่จับคู่ไว้ให้ส่ง",
    sendFailed: "ส่งไม่สำเร็จ โปรดลองอีกครั้ง",
    sendEmpty: "ไม่มีสิ่งที่จะส่ง",
    dockExpand: "แสดงประวัติคลิปบอร์ด",
    dockCollapse: "ย่อ",
    dockSendTitle: "ส่งไปยังอุปกรณ์ของคุณ",
    dockFromClipboard: (chars: number): string =>
      `จากคลิปบอร์ด · ${chars} ${selectPlural("th", chars, { other: "อักขระ" })}`,
    dockSend: (n: number): string => `ส่งไปยัง ${n}`,
    dockCancel: "ยกเลิก",
    dockRecentHeading: "ล่าสุด",
    dockEmptyClipboard: "คลิปบอร์ดของคุณว่างเปล่า",
    shareTitle: "ส่งไปยังอุปกรณ์ของคุณ",
    shareReview: "ตรวจสอบสิ่งที่คุณกำลังส่ง จะส่งไปยังอุปกรณ์ที่จับคู่ไว้ของคุณโดยเข้ารหัสจากต้นทางถึงปลายทาง",
    shareSend: "ส่ง",
    shareCancel: "ยกเลิก",
    shareNotPaired: "จับคู่อุปกรณ์ก่อนเพื่อส่งข้อความที่แชร์",
    shareOpenApp: "เปิด Reflecto",
  },
  install: {
    title: "ติดตั้ง Reflecto",
    body: "เพิ่มลงในหน้าจอหลักเพื่อเข้าถึงได้เร็วขึ้น",
    cta: "ติดตั้ง",
    dismissAria: "ปิดข้อความแจ้งให้ติดตั้ง",
  },
  common: {
    loading: "กำลังโหลด",
  },
  errors: {
    notPaired: "ยังไม่ได้จับคู่",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} บน ${os}`,
    webFallback: "เว็บเบราว์เซอร์",
  },

  palette: {
    placeholder: "พิมพ์คำสั่ง…",
    noResults: "ไม่มีคำสั่งที่ตรงกัน",
    ariaLabel: "แผงคำสั่ง",
    clearAll: "ล้างการแจ้งเตือนทั้งหมด",
    glance: "สรุป Glance",
    cancelSnooze: "ยกเลิกการหยุดชั่วคราว",
    copyClip: "คัดลอกคลิปที่ได้รับล่าสุด",
  },
} satisfies MessagesShape;
